/**
 * Enhanced Agent Controller for VRM characters
 * Provides comprehensive control over AI agents including VRM models, camera, tips, and interactions
 */

import { AIResponse, PersonalityProfile, AgentAction } from './types';
import { ConversationService } from '../services/conversation';

export interface VRMControlAction {
  type: 'vrm';
  action: 'animation' | 'expression' | 'pose' | 'gesture' | 'movement';
  value: string;
  duration?: number;
  intensity?: number;
  blendMode?: 'replace' | 'additive' | 'override';
}

export interface CameraControlAction {
  type: 'camera';
  action: 'position' | 'rotation' | 'zoom' | 'focus' | 'preset';
  value: string | number | { x: number; y: number; z: number };
  duration?: number;
  easing?: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';
}

export interface CanvasAction {
  type: 'canvas';
  action: 'draw' | 'clear' | 'overlay' | 'background' | 'effect';
  value: string;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  duration?: number;
}

export interface TipAction {
  type: 'tip';
  action: 'request' | 'goal' | 'milestone' | 'reward';
  value: string;
  amount?: number;
  currency?: 'SOL' | 'NEAR' | 'ETH';
  animation?: string;
}

export interface AudioAction {
  type: 'audio';
  action: 'play' | 'stop' | 'volume' | 'effect';
  value: string;
  volume?: number;
  fadeIn?: number;
  fadeOut?: number;
}

export type EnhancedAgentAction =
  | VRMControlAction
  | CameraControlAction
  | CanvasAction
  | TipAction
  | AudioAction;

export interface AgentState {
  personality: PersonalityProfile;
  currentEmotion: string;
  energyLevel: number; // 0-100
  tipGoals: TipGoal[];
  activeAnimations: string[];
  cameraPosition: { x: number; y: number; z: number };
  lastInteraction: Date;
  conversationContext: string[];
}

export interface TipGoal {
  id: string;
  description: string;
  targetAmount: number;
  currentAmount: number;
  currency: 'SOL' | 'NEAR' | 'ETH';
  reward: string;
  animation?: string;
  isActive: boolean;
}

export interface AgentMemory {
  userId: string;
  interactions: number;
  favoriteTopics: string[];
  tipHistory: Array<{
    amount: number;
    currency: string;
    animation: string;
    timestamp: Date;
  }>;
  preferences: Record<string, any>;
}

export class AgentController {
  private state: AgentState;
  private memory = new Map<string, AgentMemory>();
  private actionQueue: AgentAction[] = [];

  constructor(
    private conversationService: ConversationService,
    initialPersonality: PersonalityProfile
  ) {
    this.state = {
      personality: initialPersonality,
      currentEmotion: 'neutral',
      energyLevel: 80,
      tipGoals: [],
      activeAnimations: [],
      cameraPosition: { x: 0, y: 0, z: 5 },
      lastInteraction: new Date(),
      conversationContext: [],
    };
  }

  async processUserMessage(
    userId: string,
    message: string,
    metadata?: Record<string, any>
  ): Promise<AIResponse & { actions: AgentAction[] }> {
    // Update user memory
    this.updateUserMemory(userId, message);

    // Get base AI response
    const baseResponse = await this.conversationService.processMessage(
      userId,
      message,
      this.state.personality,
      {
        ...metadata,
        agentState: this.getPublicState(),
        userMemory: this.getUserMemory(userId),
      }
    );

    // Analyze message for enhanced actions
    const enhancedActions = await this.analyzeAndGenerateActions(message, baseResponse, userId);

    // Update agent state based on interaction
    this.updateAgentState(message, enhancedActions);

    const { actions: _, ...responseWithoutActions } = baseResponse;
    return {
      ...responseWithoutActions,
      actions: enhancedActions,
    };
  }

  private async analyzeAndGenerateActions(
    message: string,
    response: AIResponse,
    userId: string
  ): Promise<AgentAction[]> {
    const actions: AgentAction[] = [];
    const lowerMessage = message.toLowerCase();
    this.getUserMemory(userId);

    // VRM Actions based on conversation
    if (lowerMessage.includes('dance') || lowerMessage.includes('move')) {
      actions.push({
        type: 'vrm',
        action: 'animation',
        value: 'dance-basic',
        duration: 5000,
      });
    }

    if (lowerMessage.includes('smile') || response.emotion === 'happy') {
      actions.push({
        type: 'vrm',
        action: 'expression',
        value: 'smile',
        duration: 3000,
        intensity: 0.8,
      });
    }

    if (lowerMessage.includes('wave') || lowerMessage.includes('hello')) {
      actions.push({
        type: 'vrm',
        action: 'gesture',
        value: 'wave',
        duration: 2000,
      });
    }

    // Camera actions
    if (lowerMessage.includes('closer') || lowerMessage.includes('zoom')) {
      actions.push({
        type: 'camera',
        action: 'zoom',
        value: 3,
        duration: 2000,
        easing: 'ease-in-out',
      });
    }

    if (lowerMessage.includes('face') || lowerMessage.includes('look at me')) {
      actions.push({
        type: 'camera',
        action: 'preset',
        value: 'face-closeup',
        duration: 1500,
      });
    }

    // Tip-related actions
    if (lowerMessage.includes('tip') || lowerMessage.includes('support')) {
      actions.push({
        type: 'tip',
        action: 'request',
        value: 'Thank you for wanting to support me! Tips help me learn new animations!',
        amount: 0.1,
        currency: 'SOL',
        animation: 'thank-you',
      });
    }

    // Canvas/Image actions
    if (lowerMessage.includes('draw') || lowerMessage.includes('picture')) {
      actions.push({
        type: 'canvas',
        action: 'draw',
        value: 'Generate a cute illustration based on our conversation',
        position: { x: 0.7, y: 0.3 },
        size: { width: 300, height: 300 },
      });
    }

    // Audio actions based on emotion
    if (response.emotion === 'excited') {
      actions.push({
        type: 'audio',
        action: 'play',
        value: 'excitement-ambient',
        volume: 0.3,
        fadeIn: 1000,
      });
    }

    return actions;
  }

  private updateUserMemory(userId: string, message: string): void {
    if (!this.memory.has(userId)) {
      this.memory.set(userId, {
        userId,
        interactions: 0,
        favoriteTopics: [],
        tipHistory: [],
        preferences: {},
      });
    }

    const memory = this.memory.get(userId)!;
    memory.interactions++;
    memory.favoriteTopics = this.extractTopics(message);
  }

  private updateAgentState(message: string, actions: AgentAction[]): void {
    this.state.lastInteraction = new Date();
    this.state.conversationContext.push(message);

    // Keep only last 10 messages for context
    if (this.state.conversationContext.length > 10) {
      this.state.conversationContext = this.state.conversationContext.slice(-10);
    }

    // Update energy level based on interactions
    this.state.energyLevel = Math.min(100, this.state.energyLevel + 2);

    // Update active animations
    actions.forEach((action) => {
      if (action.type === 'vrm' && action.action === 'animation') {
        const animationValue = String(action.value);
        this.state.activeAnimations.push(animationValue);
        // Remove after duration
        setTimeout(() => {
          const index = this.state.activeAnimations.indexOf(animationValue);
          if (index > -1) {
            this.state.activeAnimations.splice(index, 1);
          }
        }, action.duration || 3000);
      }
    });
  }

  private extractTopics(message: string): string[] {
    const words = message.toLowerCase().split(' ');
    const topics = ['dance', 'music', 'art', 'games', 'anime', 'tech', 'food'];
    return topics.filter((topic) => words.includes(topic));
  }

  getUserMemory(userId: string): AgentMemory | undefined {
    return this.memory.get(userId);
  }

  getPublicState(): Omit<AgentState, 'conversationContext'> {
    const { conversationContext: _conversationContext, ...publicState } = this.state;
    return publicState;
  }

  addTipGoal(goal: Omit<TipGoal, 'id' | 'currentAmount' | 'isActive'>): string {
    const id = `goal-${Date.now()}`;
    this.state.tipGoals.push({
      ...goal,
      id,
      currentAmount: 0,
      isActive: true,
    });
    return id;
  }

  updateTipGoal(goalId: string, amount: number): boolean {
    const goal = this.state.tipGoals.find((g) => g.id === goalId);
    if (!goal) return false;

    goal.currentAmount += amount;

    if (goal.currentAmount >= goal.targetAmount) {
      goal.isActive = false;
      // Trigger reward
      this.actionQueue.push({
        type: 'vrm',
        action: 'animation',
        value: goal.animation || 'celebration',
        duration: 5000,
      });
    }

    return true;
  }

  getActiveTipGoals(): TipGoal[] {
    return this.state.tipGoals.filter((goal) => goal.isActive);
  }

  queueAction(action: AgentAction): void {
    this.actionQueue.push(action);
  }

  getNextAction(): AgentAction | undefined {
    return this.actionQueue.shift();
  }

  updatePersonality(updates: Partial<PersonalityProfile>): void {
    this.state.personality = { ...this.state.personality, ...updates };
  }

  setEmotion(emotion: string): void {
    this.state.currentEmotion = emotion;
  }

  getCameraPosition(): { x: number; y: number; z: number } {
    return this.state.cameraPosition;
  }

  setCameraPosition(position: { x: number; y: number; z: number }): void {
    this.state.cameraPosition = position;
  }
}
